import React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import IconFinance from '@mui/icons-material/MonetizationOnOutlined';
import IconAdd from '@mui/icons-material/Add';
import AreaTitle from '../../components/areaTitle/AreaTitle';
import CustomButton from '../../components/buttons/CustomButton';
import Toast from '../../components/support/Toast';
import ConfirmPopup from '../../popup/ConfirmPopup/ConfirmPopup';
import PopupBase from '../../popup/base/PopupBase';
import useTransactions from './hooks/useTransactions';
import FinanceSummary from './components/FinanceSummary';
import TransactionsFilters from './components/TransactionsFilters';
import TransactionsTable from './components/TransactionsTable';
import RecurringPanel from './components/RecurringPanel';
import TransactionForm from './components/TransactionForm';
import TransactionsSkeleton from './components/TransactionsSkeleton';
import { formatCurrency } from './utils/financeFormat';
import styles from './TransactionsPage.module.css';

/** "a descrição, o valor e a categoria": junta o que falta com vírgulas e "e" no fim. */
const joinMissing = (items) => (items.length > 1
    ? `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`
    : items[0] || '');

/** Texto da confirmação de exclusão, citando descrição e valor. */
const deleteDescription = (transaction) => {
    if (!transaction) return '';
    const base = `Excluir "${transaction.description}" (${formatCurrency(transaction.amount)})?`;
    const recurring = transaction.isRecurring ? ' Todas as repetições desta recorrência saem junto.' : '';
    return `${base}${recurring} Esta ação não pode ser desfeita.`;
};

const TransactionsPage = () => {
    const t = useTransactions();

    // Só a primeira carga troca a página pelo esqueleto; filtros novos recarregam só a lista
    if (!t.hasLoaded) {
        return <TransactionsSkeleton />;
    }

    const newButton = (
        <CustomButton
            variant="contained"
            style="main"
            label="Nova Transação"
            onClick={t.openNewForm}
            startIcon={<IconAdd />}
        />
    );

    const canSave = t.validateForm();

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <AreaTitle
                    Title="Transações"
                    Description="Entradas e saídas do período, com as recorrências ao lado."
                    Icon={<IconFinance />}
                />
                <div className={styles.actions}>{newButton}</div>
            </div>

            <FinanceSummary totals={t.totals} isLoading={t.isLoading} />

            <div className={styles.contentWrapper}>
                <div className={styles.mainContent}>
                    <TransactionsFilters
                        filters={t.filters}
                        categories={t.categories}
                        onPeriodChange={t.handlePeriodChange}
                        onFilterChange={t.handleFilterChange}
                    />
                    <TransactionsTable
                        transactions={t.transactions}
                        isLoading={t.isLoading}
                        onEdit={t.handleEdit}
                        onDelete={t.handleDelete}
                        emptyAction={newButton}
                    />
                </div>

                <div className={styles.sidebarContent}>
                    <RecurringPanel
                        items={t.recurringTransactions}
                        onEdit={t.handleEdit}
                        onDelete={t.handleDelete}
                    />
                </div>
            </div>

            {t.showForm && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <PopupBase
                        Title={t.isEditing ? 'Editar Transação' : 'Nova Transação'}
                        Content={
                            <TransactionForm
                                formData={t.formData}
                                handleFormChange={t.handleFormChange}
                                handleAmountBlur={t.handleAmountBlur}
                                amountError={t.amountError}
                                categories={t.categories}
                                handleDateChange={t.handleDateChange}
                                handleRecurrenceStartDateChange={t.handleRecurrenceStartDateChange}
                                handleRecurrenceEndDateChange={t.handleRecurrenceEndDateChange}
                                handleHasEndDateChange={t.handleHasEndDateChange}
                            />
                        }
                        Footer={
                            <div className={styles.footer}>
                                <span className={styles.footerHint}>
                                    {canSave ? '' : `Falta informar ${joinMissing(t.missing)}.`}
                                </span>
                                <CustomButton
                                    variant="outlined"
                                    style="primary"
                                    label="Cancelar"
                                    onClick={t.resetForm}
                                />
                                <CustomButton
                                    variant="contained"
                                    style="main"
                                    label={t.isEditing ? 'Atualizar' : 'Criar'}
                                    onClick={t.handleSubmit}
                                    disabled={!canSave}
                                />
                            </div>
                        }
                        OnClose={t.resetForm}
                    />
                </LocalizationProvider>
            )}

            <ConfirmPopup
                Visibility={t.showDeleteConfirm}
                Title="Excluir Transação"
                Description={deleteDescription(t.deleteTarget)}
                ConfirmLabel="Excluir"
                OnConfirm={t.confirmDelete}
                OnHide={t.cancelDelete}
            />

            <Toast
                open={t.toast.open}
                onClose={t.closeToast}
                message={t.toast.message}
                severity={t.toast.severity}
            />
        </div>
    );
};

export default React.memo(TransactionsPage);
